import { getLocalizedOrDefault } from "../../../helpers/Functions";
import { getImageUrl } from "../../../helpers/PB";

const GlobalSettingsConfig = {
  collection: "global_settings",
  single: true,
  fields: [
    "id",
    "filename",
    "default_locale.code",
    "default_locale.id",
    "show_page_numbers",

    "primary_brand.id",

    "default_audience.id",

    "comparison_page_overline_color",
    "comparison_page_heading_color",
    "comparison_page_variant_color",
    "comparison_page_subhead_color",
    "comparison_page_win_logic_highlight_color",

    "data_source_disclaimer.id",
    "gaptco_disclaimer.id",
    "product_image_proportion",
    "product_image_height_type",

    "logo.title",
    "logo.id",

    "gaptco_logo.id",
    "hide_single_option_toggles",
    "comparison_table_header_colors",
    "comparison_page_heading_overline_format.comparison_template_type",
    "comparison_page_heading_overline_format.selected_format",

    "locale.gaptco_header_text",
    "locale.decimal_separator",

    "locale.languages_code.*",
    "locale.language",
    "locale.export_link_label",
    "locale.export_link_icon.id",

    "locale.top_link_label",
    "locale.top_link_icon.id",
    "locale.page_title",
    "locale.glossary_page_headline",

    "locale.download_spec_sheet",

    "locale.gaptco_savings_default_value",
    "locale.gaptco_savings_configuration.id",
    "locale.gaptco_color_savings_configuration.id",
    "locale.gaptco_black_savings_configuration.id",

    "locale.acrobat_warning",
    "locale.acrobat_warning_icon.id",
    //for bij energy
    "modified_on",
    "created_on"
  ],

  adapter: class GlobalSettings {
    constructor(settings) {
      this.id = settings.id;
      this.date_updated = settings.modified_on || settings.created_on;
      this.filename = settings.filename;
      this.default_locale =
        settings.default_locale?.code || settings.default_locale?.id;
      this.show_page_numbers = settings.show_page_numbers;
      this.primary_brand_id = settings.primary_brand?.id;
      this.data_source_disclaimer_id = settings.data_source_disclaimer?.id;
      this.data_source_disclaimer = settings.data_source_disclaimer?.id;
      this.logo_title = settings.logo?.title;
      this.logo_url = getImageUrl(settings.logo?.id);
      this.gaptco_logo = getImageUrl(settings.gaptco_logo?.id);
      this.default_audience =
        settings.default_audience?.id || settings.default_audience;
      this.comparison_page_heading_color =
        settings.comparison_page_heading_color || "";
      this.comparison_page_overline_color =
        settings.comparison_page_overline_color || "";
      this.comparison_page_variant_color =
        settings.comparison_page_variant_color || "";
      this.comparison_page_subhead_color =
        settings.comparison_page_subhead_color || "";
      this.comparison_page_win_logic_highlight_color =
        settings.comparison_page_win_logic_highlight_color || "";

      this.comparison_page_heading_overline_format =
        settings.comparison_page_heading_overline_format || [];

      this.product_image_proportion =
        settings.product_image_proportion || "1:1";
      this.product_image_proportion_dashed =
        this.product_image_proportion.replace(/:/, "-");

      this.product_image_height_type =
        settings.product_image_height_type || "type1";
      let height_type_match = this.product_image_height_type.match(/type(\d+)/);
      // Convert "typeX" to "tX" simply to shorten class name
      if (height_type_match) {
        this.product_image_height_type_dashed = `t${height_type_match[1]}`;
      } else {
        this.product_image_height_type_dashed = this.product_image_height_type;
      }

      this.hide_single_option_toggles = !!settings.hide_single_option_toggles;
      this.comparison_table_header_colors = (
        settings.comparison_table_header_colors || []
      )
        // Extract color values
        .map(({ color }) => color)
        // Remove invalid values
        .filter((c) => !!c);

      let locale = settings.locale?.[0];
      this.locale = settings.locale;

      this.gaptco_header_text = locale?.gaptco_header_text;
      this.top_link_label = locale?.top_link_label;
      this.top_link_icon_url = getImageUrl(locale?.top_link_icon?.id);
      this.export_link_label = locale?.export_link_label;
      this.export_link_icon_url = getImageUrl(locale?.export_link_icon?.id);

      this.page_title = locale?.page_title;
      this.glossary_page_headline = locale?.glossary_page_headline;
      this.download_spec_sheet = locale?.download_spec_sheet;
      this.gaptco_disclaimer = settings.gaptco_disclaimer?.id;
      this.gaptco_savings_default_value =
        locale?.gaptco_savings_default_value || "";
      this.gaptco_savings_configuration =
        locale?.gaptco_savings_configuration?.id;
      this.gaptco_color_savings_configuration =
        locale?.gaptco_color_savings_configuration?.id;
      this.gaptco_black_savings_configuration =
        locale?.gaptco_black_savings_configuration?.id;

      this.acrobat_warning = locale?.acrobat_warning;
      this.acrobat_warning_icon = getImageUrl(locale?.acrobat_warning_icon?.id);
    }

    map(collections, maps) {
      this.primary_brand = maps.brands[this.primary_brand_id];
      this.default_audience = maps.audiences[this.default_audience]?.key;
      this.data_source_disclaimer =
        maps.disclaimers[this.data_source_disclaimer];
      this.default_locale =
        maps.languages?.[this.default_locale] ||
        maps.locales?.[this.default_locale];
      this.comparison_page_heading_overline_format =
        this.comparison_page_heading_overline_format.reduce(
          (acc, { comparison_template_type, selected_format }) => ({
            ...acc,
            [maps.comparison_template_type[comparison_template_type]?.key]:
              selected_format
          }),
          {}
        );
      this.gaptco_savings_configuration =
        maps.tco?.[this.gaptco_savings_configuration];
      this.gaptco_color_savings_configuration =
        maps.tco?.[this.gaptco_color_savings_configuration];
      this.gaptco_black_savings_configuration =
        maps.tco?.[this.gaptco_black_savings_configuration];
      this.gaptco_disclaimer = maps.disclaimers[this.gaptco_disclaimer];
      this.locale = this.locale.map((locale) => {
        const {
          language,
          languages_code,
          logo,
          gaptco_logo,
          top_link_icon,
          export_link_icon,
          acrobat_warning_icon,
          ...other_props
        } = locale;
        return {
          ...other_props,
          language:
            maps.languages?.[languages_code?.code] || maps.locales?.[language],
          logo: getImageUrl(logo?.id),
          gaptco_logo: getImageUrl(gaptco_logo?.id),
          top_link_icon: getImageUrl(top_link_icon?.id),
          export_link_icon: getImageUrl(export_link_icon?.id),
          acrobat_warning_icon: getImageUrl(acrobat_warning_icon?.id)
        };
      });

      // e.g. field_name: header
      //      selected_locale: 'es-CL'
      this.getLocalized = (field_name, selected_locale) => {
        return getLocalizedOrDefault(
          this,
          field_name,
          { locale: selected_locale },
          { locale: this.default_locale?.language || this.default_locale }
        );
      };
    }
  }
};

export default GlobalSettingsConfig;
